import type { ReactNode } from 'react';
import { Group } from '../../primitives/Group.js';
import { ReferenceChip } from '../ReferenceChip.js';
import { Stack } from '../../primitives/Stack.js';
import { Text } from '../../primitives/Text.js';
import { ImageAttachmentGrid, type ImageAttachmentView } from './ImageAttachmentGrid.js';

export type ConversationReferenceItem = {
  id: string;
  refType: string;
  refId: string;
  label?: string | null;
  snapshot?: Record<string, unknown> | null;
  createdAt?: string;
};

export type ConversationMessageItem = {
  id: string;
  body: string;
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  isInternal: boolean;
  authorUserId?: string | null;
  authorEmail?: string | null;
  authorFirstName?: string | null;
  createdAt: string;
  attachments?: ImageAttachmentView[];
  references?: ConversationReferenceItem[];
};

export type ConversationThreadProps = {
  messages: ConversationMessageItem[];
  emptyLabel?: string;
  renderTimestamp?: (iso: string) => ReactNode;
};

function messageLabel(m: ConversationMessageItem): string {
  if (m.isInternal || m.direction === 'INTERNAL') return 'Note interne';
  if (m.direction === 'INBOUND') return 'Message entrant';
  return 'Message';
}

export function ConversationThread({
  messages,
  emptyLabel = 'Aucun message pour l\'instant.',
  renderTimestamp,
}: ConversationThreadProps) {
  if (!messages.length) {
    return (
      <Text size="sm" c="dimmed">
        {emptyLabel}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {messages.map((m) => (
        <Stack
          key={m.id}
          gap="xs"
          style={{
            padding: '0.5rem 0',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              {messageLabel(m)}
              {m.authorFirstName?.trim()
                ? ` — ${m.authorFirstName.trim()}`
                : m.authorEmail
                  ? ` — ${m.authorEmail}`
                  : ''}
            </Text>
            {renderTimestamp ? (
              renderTimestamp(m.createdAt)
            ) : (
              <Text size="xs" c="dimmed">
                {new Date(m.createdAt).toLocaleString('fr-FR')}
              </Text>
            )}
          </Group>
          <Text size="sm">{m.body}</Text>
          {(m.references?.length ?? 0) > 0 ? (
            <Stack gap="xs">
              {(m.references ?? []).map((reference) => {
                const snapshot = reference.snapshot ?? {};
                const title =
                  (typeof snapshot.title === 'string' && snapshot.title.trim()) ||
                  reference.label?.trim() ||
                  reference.refType;
                const subtitle =
                  typeof snapshot.subtitle === 'string' && snapshot.subtitle.trim()
                    ? snapshot.subtitle
                    : null;
                const imageUrl =
                  typeof snapshot.imageUrl === 'string' && snapshot.imageUrl.trim()
                    ? snapshot.imageUrl
                    : null;
                return (
                  <ReferenceChip
                    key={reference.id}
                    typeLabel={reference.refType}
                    title={title}
                    subtitle={subtitle}
                    imageUrl={imageUrl}
                  />
                );
              })}
            </Stack>
          ) : null}
          {(m.attachments?.length ?? 0) > 0 ? (
            <ImageAttachmentGrid attachments={m.attachments ?? []} compact />
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
}
